import React, { useState, useEffect } from 'react';
import { MapPin, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';

const API = process.env.REACT_APP_BACKEND_URL;

// Default coordinates for India (Mumbai) - used when geolocation is unavailable
const DEFAULT_INDIA_COORDS = {
  latitude: 19.076,
  longitude: 72.8777,
  accuracy: 1000
};

/**
 * LocationGate - Full-screen mandatory location permission modal
 * Blocks app access until location permission is granted
 * Auto-bypasses for preview environments where geolocation is restricted
 */
const LocationGate = ({ children, onLocationGranted }) => {
  const [permissionStatus, setPermissionStatus] = useState('checking'); // checking, denied, granted, prompt
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [coordinates, setCoordinates] = useState(null);

  // Check if we're in a preview/restricted environment
  const isPreviewEnvironment = () => {
    const hostname = window.location.hostname;
    return hostname.includes('preview.emergentagent.com') || 
           hostname.includes('stage-preview') ||
           hostname.includes('localhost');
  };

  // Check permission status on mount
  useEffect(() => {
    checkLocationPermission();
  }, []);

  // Update backend when coordinates change and user is logged in
  useEffect(() => {
    if (coordinates) {
      updateBackendLocation(coordinates);
    }
  }, [coordinates]);

  const updateBackendLocation = async (coords) => {
    const token = secureStorage.getToken();
    if (!token || !coords) return;

    try {
      await axios.post(
        `${API}/api/auth/update-location`,
        {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[LOCATION] Backend updated with coordinates');
    } catch (err) {
      console.error('[LOCATION] Failed to update backend:', err);
      // Don't block the user if backend update fails
    }
  };

  // Apply default coordinates (for preview environment or fallback)
  const applyDefaultCoordinates = () => {
    console.log('[LOCATION] Using default India coordinates (preview/fallback mode)');
    setCoordinates(DEFAULT_INDIA_COORDS);
    setPermissionStatus('granted');
    
    // Store in localStorage
    localStorage.setItem('locationPermissionGranted', 'true');
    localStorage.setItem('userCoordinates', JSON.stringify(DEFAULT_INDIA_COORDS));
    localStorage.setItem('user_geo', JSON.stringify({
      lat: DEFAULT_INDIA_COORDS.latitude,
      lng: DEFAULT_INDIA_COORDS.longitude
    }));
    
    if (onLocationGranted) {
      onLocationGranted(DEFAULT_INDIA_COORDS);
    }
  };

  const checkLocationPermission = async () => {
    try {
      // Auto-bypass for preview environments where geolocation is restricted
      if (isPreviewEnvironment()) {
        console.log('[LOCATION] Preview environment detected - auto-granting with default coordinates');
        applyDefaultCoordinates();
        return;
      }
      
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        // Use default coordinates instead of blocking
        console.log('[LOCATION] Geolocation not supported - using defaults');
        applyDefaultCoordinates();
        return;
      }

      // Check permission status using Permissions API if available
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          
          if (result.state === 'granted') {
            // Permission already granted, get coordinates
            getCurrentPosition();
          } else if (result.state === 'denied') {
            // In preview, use defaults; otherwise show denied state
            if (isPreviewEnvironment()) {
              applyDefaultCoordinates();
            } else {
              setPermissionStatus('denied');
            }
          } else {
            // 'prompt' state - user hasn't decided yet
            setPermissionStatus('prompt');
          }

          // Listen for permission changes
          result.addEventListener('change', () => {
            if (result.state === 'granted') {
              getCurrentPosition();
            } else if (result.state === 'denied') {
              if (isPreviewEnvironment()) {
                applyDefaultCoordinates();
              } else {
                setPermissionStatus('denied');
              }
            }
          });
        } catch (permErr) {
          // Permissions API failed, try direct geolocation
          console.log('[LOCATION] Permissions API error, trying direct geolocation');
          setPermissionStatus('prompt');
        }
      } else {
        // Permissions API not available, try to get location directly
        setPermissionStatus('prompt');
      }
    } catch (err) {
      console.error('Error checking permission:', err);
      setPermissionStatus('prompt');
    }
  };

  const getCurrentPosition = () => {
    setIsRequesting(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setCoordinates(coords);
        setPermissionStatus('granted');
        setIsRequesting(false);
        
        // Notify parent component
        if (onLocationGranted) {
          onLocationGranted(coords);
        }
        
        // Store in localStorage to remember - use both keys for compatibility
        localStorage.setItem('locationPermissionGranted', 'true');
        localStorage.setItem('userCoordinates', JSON.stringify(coords));
        // Also set user_geo for PusherSessionTracker compatibility
        localStorage.setItem('user_geo', JSON.stringify({
          lat: coords.latitude,
          lng: coords.longitude
        }));
        
        console.log('[LocationGate] Precise GPS coordinates stored:', coords.latitude.toFixed(6), coords.longitude.toFixed(6));
      },
      (err) => {
        setIsRequesting(false);
        // In preview environment, use defaults instead of showing errors
        if (isPreviewEnvironment()) {
          console.log('[LOCATION] Geolocation error in preview - using defaults');
          applyDefaultCoordinates();
          return;
        }
        
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setPermissionStatus('denied');
            setError('Location access was denied. Please enable it in your browser settings.');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location information is unavailable. Please try again.');
            break;
          case err.TIMEOUT:
            setError('Location request timed out. Please try again.');
            break;
          default:
            setError('An unknown error occurred. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const handleAllowLocation = () => {
    getCurrentPosition();
  };
  
  // Skip button for preview environments
  const handleSkipLocation = () => {
    applyDefaultCoordinates();
  };

  // If permission is granted, render children
  if (permissionStatus === 'granted' || localStorage.getItem('locationPermissionGranted') === 'true') {
    return <>{children}</>;
  }

  // Show loading while checking
  if (permissionStatus === 'checking') {
    return (
      <div className="fixed inset-0 z-[99999] bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">Checking location access...</p>
        </div>
      </div>
    );
  }

  // Full-screen blocking modal
  return (
    <div className="fixed inset-0 z-[99999] bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with icon */}
        <div className="bg-red-50 p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Location Access Required
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6 leading-relaxed">
            To show you the <strong>fast-moving components</strong> and{' '}
            <strong>popular spare parts specific to your area</strong>, we need
            access to your location. Please allow location access to continue.
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Permission denied instructions */}
          {permissionStatus === 'denied' && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 mb-2 font-medium">
                Location access was blocked. To enable:
              </p>
              <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                <li>Click the lock/info icon in your browser's address bar</li>
                <li>Find "Location" in the permissions list</li>
                <li>Change it to "Allow"</li>
                <li>Refresh this page</li>
              </ol>
            </div>
          )}

          {/* Action button */}
          <Button
            onClick={handleAllowLocation}
            disabled={isRequesting}
            className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all"
          >
            {isRequesting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Getting Location...
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5 mr-2" />
                Allow Location
              </>
            )}
          </Button>

          {/* Skip button ONLY for preview/restricted environments - NOT for production */}
          {isPreviewEnvironment() && (
            <Button
              onClick={handleSkipLocation}
              variant="outline"
              className="w-full mt-3 py-4 text-gray-600 border-gray-300 hover:bg-gray-50"
            >
              Continue with Default Location (Preview Only)
            </Button>
          )}

          {/* Privacy note */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Your location data is used only to personalize your experience and
            is stored securely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LocationGate;
